import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { cartAPI, orderAPI } from '../../../services/api';
import { CartItem } from '../../../types';

export function useOrderCart() {
  const { user, isExpired } = useAuth();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [asyncState, setAsyncState] = useState<{ updating: {[key: number]: boolean}; placingOrder: boolean }>({ updating: {}, placingOrder: false });
  const [error, setError] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState({ deliveryDate: '', notes: '' });

  const leadDays = user?.min_delivery_lead_days ?? 0;
  const minDeliveryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + leadDays);
    return d.toISOString().split('T')[0];
  })();
  const deliveryRequired = leadDays > 0;

  useEffect(() => {
    fetchCartItems();
  }, []);

  const fetchCartItems = async () => {
    try {
      setLoading(true);
      const response = await cartAPI.getCartItems();
      console.log('Cart API response:', response.data);
      // Ensure we always have an array
      const cartData = Array.isArray(response.data) 
        ? response.data 
        : ((response.data as any)?.results || []);
      setCartItems(cartData);
    } catch (error) {
      console.error('Error fetching cart items:', error);
      setError('Failed to load cart items');
      setCartItems([]); // Ensure cartItems is always an array
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    try {
      setAsyncState(prev => ({ ...prev, updating: { ...prev.updating, [itemId]: true } }));
      await cartAPI.updateCartItem(itemId, newQuantity);
      // Update local state
      setCartItems(prev => 
        prev.map(item => 
          item.id === itemId 
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    } catch (error) {
      console.error('Error updating cart item:', error);
      setError('Failed to update item quantity');
    } finally {
      setAsyncState(prev => ({ ...prev, updating: { ...prev.updating, [itemId]: false } }));
    }
  };

  const removeItem = async (itemId: number) => {
    try {
      setAsyncState(prev => ({ ...prev, updating: { ...prev.updating, [itemId]: true } }));
      await cartAPI.removeFromCart(itemId);
      setCartItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error removing cart item:', error);
      setError('Failed to remove item');
    } finally {
      setAsyncState(prev => ({ ...prev, updating: { ...prev.updating, [itemId]: false } }));
    }
  };

  const handlePlaceOrder = async () => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      setError('Your cart is empty');
      return;
    }

    if (deliveryRequired && !orderDetails.deliveryDate) {
      setError('Please select a delivery date.');
      return;
    }

    try {
      setAsyncState(prev => ({ ...prev, placingOrder: true }));
      setError('');
      
      // Create order with cart item IDs, delivery date, and notes
      const cartItemIds = cartItems.map(item => item.id);
      const response = await orderAPI.createOrder(
        cartItemIds,
        orderDetails.deliveryDate || undefined,
        orderDetails.notes || undefined
      );
      
      console.log('Order created:', response.data);
      
      // Clear cart after successful order
      setCartItems([]);
      
      // Show success modal
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Error placing order:', error);
      setError('Failed to place order. Please try again.');
    } finally {
      setAsyncState(prev => ({ ...prev, placingOrder: false }));
    }
  };

  const calculateSubtotal = (item: CartItem) => {
    const price = typeof item.product.price_per_unit === 'string' 
      ? parseFloat(item.product.price_per_unit) 
      : item.product.price_per_unit;
    return price * item.quantity;
  };

  const calculateDiscountedTotal = () => {
    if (!Array.isArray(cartItems)) return 0;
    const subtotal = cartItems.reduce((sum, item) => sum + calculateSubtotal(item), 0);
    const discount = user?.discount_percentage || 0;
    return subtotal * (1 - discount / 100);
  };

  const subtotal = Array.isArray(cartItems) 
    ? cartItems.reduce((sum, item) => sum + calculateSubtotal(item), 0) 
    : 0;
  const discountAmount = subtotal - calculateDiscountedTotal();
  const total = calculateDiscountedTotal();

  return {
    user,
    isExpired,
    navigate,
    cartItems,
    loading,
    asyncState,
    error,
    orderDetails,
    setOrderDetails,
    showSuccessModal,
    setShowSuccessModal,
    deliveryRequired,
    minDeliveryDate,
    leadDays,
    updateQuantity,
    removeItem,
    handlePlaceOrder,
    calculateSubtotal,
    subtotal,
    discountAmount,
    total,
  };
}
